import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Checkbox from "@mui/material/Checkbox";
import Dialog from "@mui/material/Dialog";
import DialogActions from "@mui/material/DialogActions";
import DialogContent from "@mui/material/DialogContent";
import DialogTitle from "@mui/material/DialogTitle";
import LinearProgress from "@mui/material/LinearProgress";
import List from "@mui/material/List";
import TextField from "@mui/material/TextField";
import Typography from "@mui/material/Typography";
import {
  EMPTY_ITEM_FACETS,
  type DeadlineFilter,
  type Item,
  type ItemFacets,
  type ItemSort,
} from "@egalregal/shared";
import { requests } from "../../shared/api";
import { useT } from "../../shared/i18n";
import { usePreferencesStore } from "../../shared/store/preferences";
import { readableContainers, useAppStore } from "../../shared/store/app";
import { useOpenContainer } from "../../shared/store/useOpenContainer";
import { canWriteContainer } from "../catalogs/access";
import { PlacementQuantitySnackbar } from "./PlacementQuantitySnackbar";
import { usePlacementQuantity } from "./usePlacementQuantity";
import { FilterTable, type FilterRow } from "../../shared/components/FilterTable";
import { itemCategoryLabel } from "../../shared/components/CategoryChip";
import {
  readItemFilters,
  writeItemFilters,
  type ItemFilterState,
} from "./itemFilterUrl";
import { ItemListToolbar } from "./ItemListToolbar";
import { itemToSearchHit } from "./itemHit";
import { describeFacets, toggleFacetValue } from "../search/facets";
import { getMirroredPlacements } from "../search/catalogStore";
import { PreviewPane } from "../search/PreviewPane";
import { ResultRow } from "../search/ResultRow";
import type { SearchHit } from "../search/searchHits";
import { CreateItemDialog } from "./CreateItemDialog";
import { BulkActionBar } from "./BulkActionBar";
import { DeadlineColumn, DeadlineColumnHeader } from "../../shared/components/Deadlines";
import { useDemoMode } from "../demo/useDemoMode";

const PAGE_SIZE = 30;

/** Stable empty selection when the deadline view (a preference) is off. */
const EMPTY_DEADLINE: DeadlineFilter[] = [];

/**
 * Paginated list of all items with the shared filter table, variant-A result
 * disclosure and bulk edit mode (filter-table-reuse 02). Every row expands to
 * level 2 on click; the trailing arrow opens the item. In bulk mode a row click
 * selects instead (the arrow still opens the item).
 *
 * The filter table's owner/tag/category rows are server-backed: the API joins
 * placements to containers so an item inherits its host's owner/tags, and it
 * returns contextual option counts. Infinite scroll via IntersectionObserver.
 */
export function ItemListView() {
  const t = useT();
  const demoMode = useDemoMode();
  const navigate = useNavigate();
  const openContainer = useOpenContainer();
  const [searchParams, setSearchParams] = useSearchParams();
  // Derive from the serialized params so the arrays stay referentially stable
  // between renders and the refetch effect only runs on a real filter change.
  const paramsKey = searchParams.toString();
  const urlFilters = useMemo(
    () => readItemFilters(new URLSearchParams(paramsKey)),
    [paramsKey],
  );

  const [items, setItems] = useState<Item[]>([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(0);
  const [loading, setLoading] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const [appliedQuery, setAppliedQuery] = useState("");
  // Unified filter table (filter-table-reuse 02): OR within a property, AND
  // across properties; the options/counts come from the API. The selection
  // itself is URL-owned (ticket 10).
  const [facetOptions, setFacetOptions] = useState<ItemFacets>(EMPTY_ITEM_FACETS);
  const [previewHit, setPreviewHit] = useState<SearchHit | null>(null);
  // Opt-in deadline view (deadlines 02, verdict F): persisted per user, off by
  // default. The toolbar counts come from the server over the search-filtered
  // set before the deadline filter narrows the page (deadlines 04). Only the
  // filter value is URL-owned; the preference itself is not.
  const deadlineView = usePreferencesStore((s) => s.itemDeadlineView);
  const setDeadlineView = usePreferencesStore((s) => s.setItemDeadlineView);
  const [counts, setCounts] = useState<{ overdue: number; soon: number }>({ overdue: 0, soon: 0 });
  const entries = useAppStore((s) => s.entries);
  const sharedWithMe = useAppStore((s) => s.sharedWithMe);
  const selectedEntryId = useAppStore((s) => s.selectedEntryId);
  // Bumped after a placement removal/undo so the row re-reads the mirror.
  const [mirrorVersion, setMirrorVersion] = useState(0);
  const refreshMirror = useCallback(() => setMirrorVersion((version) => version + 1), []);
  const { notice, dismiss, quantityOf, change, undo } = usePlacementQuantity(refreshMirror);
  const { sort, owners, tags, categories, showPhotos } = urlFilters;
  const deadlineFilters = deadlineView ? urlFilters.deadline : EMPTY_DEADLINE;

  // URL writes always replace: filter changes never stack history entries.
  // Deadline params are dropped whenever the deadline view is off so a hidden
  // filter cannot narrow the list.
  const updateFilters = (patch: Partial<ItemFilterState>) => {
    setSearchParams(
      (prev) => {
        const current = readItemFilters(prev);
        const next = { ...current, ...patch };
        if (!deadlineView) next.deadline = [];
        return writeItemFilters(prev, next);
      },
      { replace: true },
    );
  };

  const [bulkMode, setBulkMode] = useState(false);
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [refreshing, setRefreshing] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [deleting, setDeleting] = useState(false);

  const [scanOpen, setScanOpen] = useState(false);

  const sentinelRef = useRef<HTMLDivElement>(null);
  const hasMore = items.length < total;
  const hasLoaded = useRef(false);

  // ── Data fetching ────────────────────────────────────────────────

  const loadPage = useCallback(
    async (
      p: number,
      q: string,
      s: ItemSort,
      filters: readonly DeadlineFilter[],
      propertyFilters: { owners: readonly string[]; tags: readonly string[]; categories: readonly string[] },
      append: boolean,
    ) => {
      setLoading(true);
      try {
        const res = await requests.items.listAll({
          q: q || undefined,
          sort: s,
          page: p,
          limit: PAGE_SIZE,
          deadline: filters,
          owners: propertyFilters.owners,
          tags: propertyFilters.tags,
          categories: propertyFilters.categories,
        });
        setItems((prev) => (append ? [...prev, ...res.items] : res.items));
        setTotal(res.total);
        setCounts(res.counts);
        setFacetOptions(res.facets ?? EMPTY_ITEM_FACETS);
        setPage(p);
      } finally {
        setLoading(false);
        hasLoaded.current = true;
      }
    },
    [],
  );

  // Refetch when search or sort changes
  const searchTimerRef = useRef<ReturnType<typeof setTimeout>>(undefined);
  const handleSearchChange = (value: string) => {
    setSearchQuery(value);
    if (searchTimerRef.current) clearTimeout(searchTimerRef.current);
    searchTimerRef.current = setTimeout(() => {
      setAppliedQuery(value);
      setSelected(new Set());
    }, 300);
  };

  useEffect(() => {
    loadPage(0, appliedQuery, sort, deadlineFilters, { owners, tags, categories }, false);
  }, [appliedQuery, sort, deadlineFilters, owners, tags, categories, loadPage]);

  // ── Infinite scroll ──────────────────────────────────────────────

  useEffect(() => {
    const el = sentinelRef.current;
    if (!el) return;
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0]?.isIntersecting && hasMore && !loading) {
          loadPage(page + 1, appliedQuery, sort, deadlineFilters, { owners, tags, categories }, true);
        }
      },
      { rootMargin: "200px" },
    );
    observer.observe(el);
    return () => observer.disconnect();
  }, [hasMore, loading, page, appliedQuery, sort, deadlineFilters, owners, tags, categories, loadPage]);

  // ── Sort ─────────────────────────────────────────────────────────

  const handleSort = (value: ItemSort | null) => {
    if (value) {
      updateFilters({ sort: value });
      setSelected(new Set());
    }
  };

  const toggleOwner = (value: string) => {
    updateFilters({ owners: toggleFacetValue(owners, value) });
    setSelected(new Set());
  };
  const toggleTag = (value: string) => {
    updateFilters({ tags: toggleFacetValue(tags, value) });
    setSelected(new Set());
  };
  const toggleCategory = (value: string) => {
    updateFilters({ categories: toggleFacetValue(categories, value) });
    setSelected(new Set());
  };

  const propertyFilterCount = owners.length + tags.length + categories.length;
  const activeFacetCount = propertyFilterCount + deadlineFilters.length;

  const clearFacets = () => {
    // The table's Clear all also clears the deadline row (ticket 07).
    updateFilters({ owners: [], tags: [], categories: [], deadline: [] });
    setSelected(new Set());
  };

  const handleDeadlineViewChange = (enabled: boolean) => {
    setDeadlineView(enabled);
    // Turning the deadline view off returns rows to their plain name layout
    // and drops the filter chrome with it.
    if (!enabled) {
      updateFilters({
        deadline: [],
        sort: sort === "soonest" ? "name" : sort,
      });
    }
  };

  const handleDeadlineFiltersChange = (filters: DeadlineFilter[]) => {
    updateFilters({ deadline: filters });
    setSelected(new Set());
  };

  const toggleDeadlineSort = () => {
    updateFilters({ sort: sort === "soonest" ? "name" : "soonest" });
    setSelected(new Set());
  };

  // ── Bulk edit ────────────────────────────────────────────────────

  const toggleBulkMode = () => {
    setBulkMode((b) => !b);
    setSelected(new Set());
  };

  const toggleSelect = (id: string) => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const toggleSelectAll = () => {
    if (selected.size === items.length) {
      setSelected(new Set());
    } else {
      setSelected(new Set(items.map((i) => i.id)));
    }
  };

  const selectedIds = Array.from(selected);

  const handleRefresh = async () => {
    const batch = selectedIds.slice(0, 10);
    setRefreshing(true);
    try {
      await requests.items.refresh(batch);
      loadPage(0, appliedQuery, sort, deadlineFilters, { owners, tags, categories }, false);
    } finally {
      setRefreshing(false);
      setSelected(new Set());
    }
  };

  const handleBulkDelete = async () => {
    setDeleting(true);
    try {
      await requests.items.bulkDelete(selectedIds);
      loadPage(0, appliedQuery, sort, deadlineFilters, { owners, tags, categories }, false);
    } finally {
      setDeleting(false);
      setDeleteDialogOpen(false);
      setBulkMode(false);
      setSelected(new Set());
    }
  };

  // ── Derived rows + filter table ──────────────────────────────────

  const pool = useAppStore(readableContainers);
  const hits = useMemo(
    () => items.map((item) => itemToSearchHit(item, pool, getMirroredPlacements())),
    [items, pool, mirrorVersion],
  );
  const preview = previewHit
    ? (hits.find((hit) => hit.key === previewHit.key) ?? null)
    : null;
  const sortLabel =
    sort === "name"
      ? t("sortAlpha")
      : sort === "newest"
        ? t("sortNewest")
        : sort === "category"
          ? t("sortCategory")
          : t("sortSoonest");

  const facetRows: FilterRow[] = [
    {
      key: "owner",
      label: t("filterOwner"),
      values: facetOptions.owners.map((option) => ({
        value: option.value,
        label: option.value || t("filterUnassigned"),
        count: option.count,
        selected: owners.includes(option.value),
        testId: `owner-${option.value || "unassigned"}`,
      })),
      onSelect: toggleOwner,
    },
    {
      key: "tag",
      label: t("filterTag"),
      values: facetOptions.tags.map((option) => ({
        value: option.value,
        label: option.value,
        count: option.count,
        selected: tags.includes(option.value),
        testId: `tag-${option.value}`,
      })),
      onSelect: toggleTag,
    },
    {
      key: "category",
      label: t("filterType"),
      values: facetOptions.categories.map((option) => ({
        value: option.value,
        label: itemCategoryLabel(option.value, t),
        count: option.count,
        selected: categories.includes(option.value),
        testId: `category-${option.value}`,
      })),
      onSelect: toggleCategory,
    },
    ...(deadlineView
      ? [
          {
            key: "deadline",
            label: t("deadlineFilters"),
            values: [
              {
                value: "any",
                label: t("filterAny"),
                selected: deadlineFilters.length === 0,
                testId: "deadline-any",
              },
              {
                value: "overdue",
                label: t("deadlineOverdue"),
                count: counts.overdue,
                selected: deadlineFilters.includes("overdue"),
                testId: "deadline-overdue",
              },
              {
                value: "soon",
                label: t("deadlineSoon"),
                count: counts.soon,
                selected: deadlineFilters.includes("soon"),
                testId: "deadline-soon",
              },
            ],
            onSelect: (value: string) => {
              if (value === "any") {
                handleDeadlineFiltersChange([]);
                return;
              }
              const filter = value as DeadlineFilter;
              const next = deadlineFilters.includes(filter)
                ? deadlineFilters.filter((entry) => entry !== filter)
                : [...deadlineFilters, filter];
              handleDeadlineFiltersChange(next);
            },
          },
        ]
      : []),
    {
      key: "sort",
      label: t("sortAlpha"),
      values: (["name", "newest", "category", "soonest"] as const).map((value) => ({
        value,
        label:
          value === "name"
            ? t("sortAlpha")
            : value === "newest"
              ? t("sortNewest")
              : value === "category"
                ? t("sortCategory")
                : t("sortSoonest"),
        selected: sort === value,
        testId: `sort-${value}`,
      })),
      onSelect: (value) => handleSort(value as ItemSort),
    },
  ];

  const deadlineSummary = deadlineFilters
    .map((filter) => (filter === "overdue" ? t("deadlineOverdue") : t("deadlineSoon")))
    .join(", ");
  const summary = [
    activeFacetCount > 0
      ? describeFacets(
          { owners, tags, types: categories },
          (facet, value) => (facet === "types" ? itemCategoryLabel(value, t) : value),
        )
      : "",
    `Sort: ${sortLabel}`,
    deadlineSummary === "" ? "" : `${t("deadlineLabel")}: ${deadlineSummary}`,
    showPhotos ? "" : `${t("itemPhotos")}: ${t("filterHide")}`,
  ]
    .filter(Boolean)
    .join(" · ");

  // ── Render ───────────────────────────────────────────────────────

  return (
    <Box sx={{ display: "flex", flexDirection: "column", height: "100%" }}>
      <ItemListToolbar
        bulkMode={bulkMode}
        onToggleBulk={toggleBulkMode}
        onScan={() => setScanOpen(true)}
        deadlineView={deadlineView}
        onDeadlineViewChange={handleDeadlineViewChange}
        overdue={counts.overdue}
        soon={counts.soon}
      />

      {/* Search */}
      <Box sx={{ px: 1, pb: 1 }}>
        <TextField
          fullWidth
          size="small"
          placeholder={t("searchPlaceholderItems")}
          value={searchQuery}
          onChange={(e) => handleSearchChange(e.target.value)}
        />
      </Box>

      <Box sx={{ px: 1, pb: 1 }}>
        <FilterTable
          rows={facetRows}
          displayRows={[
            {
              key: "photos",
              label: t("itemPhotos"),
              shown: showPhotos,
              onChange: (shown: boolean) => updateFilters({ showPhotos: shown }),
            },
          ]}
          activeCount={activeFacetCount}
          summary={summary}
          onClear={clearFacets}
        />
      </Box>


      {loading && items.length === 0 && hasLoaded.current && <LinearProgress />}

      {/* List */}
      {items.length === 0 && !loading ? (
        <Typography variant="body1" color="text.secondary" sx={{ p: 2 }}>
          {propertyFilterCount > 0
            ? t("noMatches")
            : deadlineFilters.length > 0
              ? t("deadlineFilterEmpty")
              : t("itemListEmpty")}
        </Typography>
      ) : (
        <List dense disablePadding>
          {deadlineView && (
            <DeadlineColumnHeader
              label={t("itemsList")}
              soonest={sort === "soonest"}
              onToggleSort={toggleDeadlineSort}
            />
          )}
          {bulkMode && (
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                gap: 1,
                px: 1,
                py: 0.5,
                borderBottom: 1,
                borderColor: "divider",
              }}
            >
              <Checkbox
                edge="start"
                checked={selected.size === items.length && items.length > 0}
                indeterminate={selected.size > 0 && selected.size < items.length}
                onClick={toggleSelectAll}
              />
              <Typography variant="body2">{t("selectItems")}</Typography>
            </Box>
          )}
          {hits.map((hit) => {
            if (hit.result.type !== "item") return null;
            const item = hit.result.item;
            const containerId = hit.result.containerId;
            const quantity = containerId
              ? quantityOf(containerId, item.id, hit.quantity)
              : hit.quantity;
            const singlePlacement = hit.placements.length === 1;
            return (
              <ResultRow
                key={hit.key}
                hit={hit}
                display={{ previews: showPhotos, descriptions: true }}
                bulkMode={bulkMode}
                selected={selected.has(item.id)}
                onSelect={() => toggleSelect(item.id)}
                onOpenContainer={
                  singlePlacement && containerId
                    ? () => void openContainer(containerId)
                    : undefined
                }
                quantity={quantity}
                canWrite={
                  !demoMode &&
                  (containerId
                    ? canWriteContainer(
                        pool,
                        entries,
                        sharedWithMe,
                        containerId,
                        selectedEntryId,
                      )
                    : true)
                }
                onQuantityChange={
                  !demoMode && singlePlacement && containerId && quantity !== undefined
                    ? (next) =>
                        void change({
                          item,
                          containerId,
                          next,
                        })
                    : undefined
                }
                trailing={
                  deadlineView ? (
                    <Box sx={{ width: { xs: 148, sm: 200 } }}>
                      <DeadlineColumn properties={item.properties} />
                    </Box>
                  ) : undefined
                }
                hideDeadlineDot={deadlineView}
                onOpen={() => navigate(`/items/${item.id}`)}
                onPreview={() => setPreviewHit(hit)}
              />
            );
          })}
        </List>
      )}

      {/* Infinite scroll sentinel */}
      <div ref={sentinelRef} style={{ height: 1 }} />
      {loading && items.length > 0 && <LinearProgress />}

      {/* Bulk action bar */}
      {!demoMode && bulkMode && selected.size > 0 && (
        <BulkActionBar
          count={selectedIds.length}
          refreshing={refreshing}
          onRefresh={() => void handleRefresh()}
          onDelete={() => setDeleteDialogOpen(true)}
          onClear={() => setSelected(new Set())}
        />
      )}

      {/* Delete confirmation dialog */}
      <Dialog open={!demoMode && deleteDialogOpen} onClose={() => setDeleteDialogOpen(false)}>
        <DialogTitle>{t("bulkDeleteConfirmTitle")}</DialogTitle>
        <DialogContent>
          <Typography>
            {selectedIds.length === 1
              ? t("bulkDeleteConfirmBodyOne", { count: 1 })
              : t("bulkDeleteConfirmBody", { count: selectedIds.length })}
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteDialogOpen(false)} disabled={deleting}>
            {t("cancel")}
          </Button>
          <Button color="error" onClick={handleBulkDelete} disabled={deleting}>
            {t("delete")}
          </Button>
        </DialogActions>
      </Dialog>

      {!demoMode && (
        <CreateItemDialog
          open={scanOpen}
          onClose={() => setScanOpen(false)}
          onCreated={() =>
            loadPage(0, appliedQuery, sort, deadlineFilters, { owners, tags, categories }, false)
          }
        />
      )}

      <PlacementQuantitySnackbar
        notice={notice}
        onClose={dismiss}
        onUndo={() => void undo()}
      />

      <PreviewPane
        hit={preview}
        openLabel={t("goToItem")}
        onOpen={() => {
          if (preview?.result.type === "item") {
            setPreviewHit(null);
            navigate(`/items/${preview.result.item.id}`);
          }
        }}
        onClose={() => setPreviewHit(null)}
      />
    </Box>
  );
}